import { PaidByPartnerHospital, ResultStage, StudentLoanCalculationParameters } from 'api/StudentLoanApi';
import Employer from 'components/StudentLoanForgiveness/Questions/Employer';
import Work from 'components/StudentLoanForgiveness/Questions/Work';
import Household from 'components/StudentLoanForgiveness/Questions/Household';
import Income from 'components/StudentLoanForgiveness/Questions/Income';
import Estimate from 'components/StudentLoanForgiveness/Estimate/Estimate';
import Plan from 'components/StudentLoanForgiveness/Plan/Plan';
import Steps from 'components/StudentLoanForgiveness/Steps/Steps';
import Later from 'components/StudentLoanForgiveness/Later/Later';
import Blocked from 'components/StudentLoanForgiveness/Blocked/Blocked';
import NotEligible from 'components/StudentLoanForgiveness/NotEligible';
import ApplyStepsIntro from 'components/StudentLoanForgiveness/Apply/ApplyStepsIntro';
import { ApplyNavigation } from 'components/StudentLoanForgiveness/Apply/applySteps';
import ApplyStepsCompleted from 'components/StudentLoanForgiveness/Apply/ApplyStepsCompleted';
import ServiceAgreementDone from 'components/StudentLoanForgiveness/Apply/Steps/ServiceAgreement/ServiceAgreementDone';
import UploadLoanInformation from 'components/StudentLoanForgiveness/UploadLoanInformation';
import ExploreMore from 'components/StudentLoanForgiveness/ExploreMore/ExploreMore';

import { RoutePath } from 'enums/Routes';
import {
  BlockedScreenResult,
  EstimateScreenResult,
  LaterScreenResult,
  PlanScreenResult,
  UploadLoanInformationResult,
} from 'enums/StudentLoanForgivenessFlowResults';
import { StepsResult } from 'enums/FlowNextResults';
import { StudentLoanDataState } from 'handlers/studentLoanData';

import { NextFunction, RouterType } from './types';

/** Employer answered via CMS match / facility pick, or an employer-type selection. */
const isEmployerAnswered = (answers: StudentLoanCalculationParameters): boolean =>
  answers.employerType != null ||
  answers.paidByPartnerHospital === PaidByPartnerHospital.Yes ||
  answers.paidByPartnerHospital === PaidByPartnerHospital.NotSure ||
  Boolean(answers.selectedHospitalFacilityId);

export const areStudentLoanAnswersComplete = (answers: StudentLoanCalculationParameters): boolean =>
  isEmployerAnswered(answers) &&
  answers.hoursPerWeek != null &&
  answers.filingStatus != null &&
  answers.dependents !== undefined &&
  answers.agi != null;

export const firstMissingQuestionRoute = (answers: StudentLoanCalculationParameters): RoutePath => {
  if (!isEmployerAnswered(answers)) return RoutePath.StudentLoanEmployer;
  if (answers.hoursPerWeek == null) return RoutePath.StudentLoanWork;
  if (answers.filingStatus == null || answers.dependents === undefined) return RoutePath.StudentLoanHousehold;
  if (answers.agi == null) return RoutePath.StudentLoanIncome;
  return RoutePath.StudentLoanEmployer;
};

const title = 'Student Loan Forgiveness';
const QUESTION_STEP_COUNT = 4;

/** Terminal stages take over the flow wherever the borrower is; otherwise continue to `fallback`. */
const getStageRoute = (stage: unknown, fallback: RoutePath): RoutePath => {
  switch (stage) {
    case ResultStage.NoFederalLoans:
      return RoutePath.StudentLoanNotEligible;
    case ResultStage.Blocked:
      return RoutePath.StudentLoanBlocked;
    default:
      return fallback;
  }
};

export const getStudentLoanForgivenessRoutes = (next: NextFunction): RouterType => ({
  [RoutePath.StudentLoanEmployer]: {
    navigationInfo: { showBackLink: false, title, step: 1, stepCount: QUESTION_STEP_COUNT },
    component: Employer,
    handleNext: ({ navigate }) => (stage) => {
      navigate(getStageRoute(stage, RoutePath.StudentLoanWork));
    },
  },
  [RoutePath.StudentLoanWork]: {
    navigationInfo: { showBackLink: true, title, step: 2, stepCount: QUESTION_STEP_COUNT },
    component: Work,
    handleNext: ({ navigate }) => (stage) => {
      navigate(getStageRoute(stage, RoutePath.StudentLoanHousehold));
    },
  },
  [RoutePath.StudentLoanHousehold]: {
    navigationInfo: { showBackLink: true, title, step: 3, stepCount: QUESTION_STEP_COUNT },
    component: Household,
    handleNext: ({ navigate }) => (stage) => {
      navigate(getStageRoute(stage, RoutePath.StudentLoanIncome));
    },
  },
  [RoutePath.StudentLoanIncome]: {
    navigationInfo: { showBackLink: true, title, step: 4, stepCount: QUESTION_STEP_COUNT },
    component: Income,
    handleNext: ({ navigate }) => (stage) => {
      navigate(
        getStageRoute(stage, stage === ResultStage.Plan ? RoutePath.StudentLoanPlan : RoutePath.StudentLoanEstimate),
      );
    },
  },
  [RoutePath.StudentLoanEstimate]: {
    navigationInfo: { showBackLink: true, title },
    component: Estimate,
    handleNext: (args) => (result) => {
      if (result === EstimateScreenResult.Refine) {
        args.navigate(RoutePath.StudentLoanUploadAidData);
      } else {
        args.navigate(RoutePath.StudentLoanLater);
      }
    },
  },
  [RoutePath.StudentLoanUploadAidData]: {
    navigationInfo: { showBackLink: true, title },
    component: UploadLoanInformation,
    handleNext: ({ navigate }) => (result) => {
      if (result === UploadLoanInformationResult.NotNow) {
        navigate(RoutePath.StudentLoanLater);
        return;
      }
      navigate(
        getStageRoute(result, result === ResultStage.Plan ? RoutePath.StudentLoanPlan : RoutePath.StudentLoanEstimate),
      );
    },
  },
  [RoutePath.StudentLoanPlan]: {
    navigationInfo: { showBackLink: false, title },
    component: Plan,
    handleNext: (args) => (result) => {
      if (result === PlanScreenResult.SeeSteps) {
        args.navigate(RoutePath.StudentLoanSteps);
      } else {
        next(args)(undefined);
      }
    },
  },
  [RoutePath.StudentLoanSteps]: {
    navigationInfo: { showBackLink: true, title },
    component: Steps,
    handleNext: ({ navigate }) => () => {
      navigate(RoutePath.StudentLoanExploreMore);
    },
  },
  [RoutePath.StudentLoanExploreMore]: {
    navigationInfo: { showBackLink: true, title },
    component: ExploreMore,
    handleNext: (args) => () => {
      next(args)(undefined);
    },
  },
  [RoutePath.StudentLoanLater]: {
    navigationInfo: { showBackLink: false, title },
    component: Later,
    handleNext: (args) => (result) => {
      if (result === LaterScreenResult.UploadNow) {
        args.navigate(RoutePath.StudentLoanUploadAidData);
      } else {
        next(args)(undefined);
      }
    },
  },
  [RoutePath.StudentLoanBlocked]: {
    navigationInfo: { showBackLink: false, title },
    component: Blocked,
    handleNext: (args) => (result) => {
      if (result === BlockedScreenResult.UploadNewFile) {
        args.navigate(RoutePath.StudentLoanUploadAidData);
      } else {
        next(args)(undefined);
      }
    },
  },
  [RoutePath.StudentLoanNotEligible]: {
    navigationInfo: { showBackLink: false },
    component: NotEligible,
    handleNext: next,
  },
  [RoutePath.StudentLoanApplyStepsIntro]: {
    navigationInfo: { showBackLink: true, title },
    component: ApplyStepsIntro,
    handleNext: ({ navigate }) => () => {
      navigate(RoutePath.StudentLoanApplySteps);
    },
  },
  [RoutePath.StudentLoanApplySteps]: {
    component: ApplyNavigation,
    handleNext: ({ navigate }) => async (result) => {
      switch (result) {
        case StepsResult.Completed:
          navigate(RoutePath.StudentLoanApplyStepsCompleted);
          break;
        default:
          navigate(RoutePath.Error);
          break;
      }
    },
  },
  [RoutePath.StudentLoanApplyStepsCompleted]: {
    navigationInfo: { showBackLink: true, title },
    component: ApplyStepsCompleted,
    handleNext: next,
  },
  [RoutePath.StudentLoanServiceAgreementDone]: {
    navigationInfo: { showBackLink: false },
    component: ServiceAgreementDone,
    handleNext: ({ navigate }) => (result) => {
      switch (result) {
        case StepsResult.Completed:
          navigate(RoutePath.StudentLoanApplyStepsCompleted);
          break;
        default:
          navigate(RoutePath.StudentLoanApplySteps);
          break;
      }
    },
  },
});

export const getStudentLoanStart = ({ result, answers }: StudentLoanDataState) => {
  switch (result?.stage) {
    case ResultStage.NoFederalLoans:
      return RoutePath.StudentLoanNotEligible;
    case ResultStage.Blocked:
      return RoutePath.StudentLoanBlocked;
    case ResultStage.Plan:
      return RoutePath.StudentLoanPlan;
    case ResultStage.Estimate:
      return areStudentLoanAnswersComplete(answers)
        ? RoutePath.StudentLoanEstimate
        : firstMissingQuestionRoute(answers);
    default:
      return firstMissingQuestionRoute(answers);
  }
};
